import { Link } from "react-router-dom";
import { ArrowRight, Compass, MapPin, Users } from "lucide-react";
import NewsletterSignup from "@/components/NewsletterSignup";
import { useLang } from "@/i18n";
import IntlCurrencySelector from "@/components/intl/IntlCurrencySelector";
import { useIntlCurrency } from "@/contexts/IntlCurrencyContext";
import { formatIntlPrice } from "@/lib/intlPricing";
import { Button } from "@/components/ui/button";

type IntlHomeCopy = {
  eyebrow: string;
  headline: string;
  body: string;
  primary: string;
  secondary: string;
  values: string[];
  mapEyebrow: string;
  mapHeadline: string;
  soloTitle: string;
  soloBody: string;
  couplesTitle: string;
  couplesBody: string;
  manifestoEyebrow: string;
  manifesto: string;
  newsletterEyebrow: string;
  newsletterTitle: string;
  newsletterBody: string;
};

const IntlHome = () => {
  const { dictionary, route, t } = useLang();
  const { currency } = useIntlCurrency();
  const home = dictionary.intlHome as IntlHomeCopy;
  const maps = [
    { title: home.soloTitle, body: home.soloBody, href: route("mapSolo"), price: formatIntlPrice("map_single", currency), icon: MapPin, tone: "bg-terracotta/10" },
    { title: "Career Map", body: "A focused document for making a decision about work and your next direction.", href: route("mapCareer"), price: formatIntlPrice("career_map", currency), icon: Compass, tone: "bg-mustard/20" },
    { title: home.couplesTitle, body: home.couplesBody, href: route("couplesMap"), price: formatIntlPrice("couples_map", currency), icon: Users, tone: "bg-ink/[0.08]" },
  ];

  return (
    <>
      <section className="min-h-[86vh] pt-32 pb-20 px-6 bg-cream flex items-center">
        <div className="container mx-auto grid lg:grid-cols-[1.05fr_0.95fr] gap-12 items-center">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.28em] uppercase text-terracotta font-semibold mb-6">{home.eyebrow}</p>
            <h1 className="font-marquee text-5xl md:text-7xl lg:text-8xl font-black tracking-tight text-ink leading-[0.95]">
              {home.headline}
            </h1>
            <p className="mt-7 text-lg md:text-xl text-ink/72 leading-relaxed max-w-2xl">{home.body}</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link
                to={route("archetype")}
                className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-7 py-3.5 text-sm font-semibold tracking-[0.08em] uppercase hover:bg-terracotta/90 transition"
              >
                {home.primary} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to={route("map")}
                className="inline-flex items-center rounded-full border border-ink/25 px-7 py-3.5 text-sm text-ink hover:bg-ink/5 transition"
              >
                {home.secondary}
              </Link>
            </div>
          </div>
          <div className="relative min-h-[360px]">
            <div className="relative rounded-3xl border border-ink/10 bg-white/50 p-8 shadow-xl">
              <svg viewBox="0 0 460 300" className="w-full h-auto" aria-hidden>
                <path d="M 56 238 C 130 82 228 100 280 55 S 388 64 413 194" stroke="hsl(var(--ink))" strokeWidth="7" fill="none" strokeLinecap="round" />
                <path d="M 74 225 C 150 258 238 240 315 202 S 384 126 428 93" stroke="hsl(var(--terracotta))" strokeWidth="5" fill="none" strokeLinecap="round" opacity="0.8" />
                <circle cx="92" cy="205" r="18" fill="hsl(var(--terracotta))" />
                <circle cx="282" cy="55" r="13" fill="hsl(var(--mustard))" />
                <circle cx="414" cy="193" r="11" fill="hsl(var(--ink))" />
              </svg>
              <p className="mt-5 font-serif text-2xl text-ink leading-snug">{home.manifesto}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y hairline bg-cream">
        <div className="container mx-auto px-6 py-6">
          <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {home.values.map((item: string, index: number) => (
              <li key={item} className="flex items-center gap-3 text-ink/82">
                <span className={index % 2 === 0 ? "block h-3 w-3 rounded-full bg-terracotta" : "block h-3 w-3 rounded-full bg-mustard"} />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-cream py-24 px-6">
        <div className="container mx-auto max-w-5xl">
          <p className="text-xs tracking-[0.25em] uppercase text-terracotta font-semibold">{home.mapEyebrow}</p>
          <h2 className="font-marquee text-3xl md:text-5xl font-bold mt-3 max-w-3xl leading-tight">{home.mapHeadline}</h2>
           <div className="mt-6 flex justify-end"><IntlCurrencySelector /></div>
           <div className="grid lg:grid-cols-3 gap-6 mt-6">
             {maps.map(({ title, body, href, price, icon: Icon, tone }) => (
               <article key={title} className="rounded-3xl border border-ink/10 bg-white/50 overflow-hidden flex flex-col">
                 <div className={`${tone} px-7 py-8 border-b border-ink/10`}><Icon className="h-6 w-6 text-terracotta" /></div>
                 <div className="p-7 flex flex-col flex-1">
                   <h3 className="font-serif text-3xl lg:min-h-[4.5rem]">{title}</h3>
                   <p className="mt-4 text-ink/70 leading-relaxed flex-1">{body}</p>
                   <div className="mt-6 pt-5 border-t border-ink/10">
                     <div className="font-serif text-3xl">{price}</div>
                     <div className="text-sm text-ink/60 mt-1">One-time payment</div>
                     <Button asChild className="mt-5 w-full rounded-full bg-terracotta text-cream hover:bg-terracotta/90">
                       <Link to={href}>{t("newsletter.read")} <ArrowRight className="h-4 w-4" /></Link>
                     </Button>
                   </div>
                 </div>
               </article>
             ))}
          </div>
        </div>
      </section>

      <section className="bg-ink text-cream py-24 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs tracking-[0.25em] uppercase text-cream/45 font-semibold">{home.manifestoEyebrow}</p>
          <p className="font-marquee text-3xl md:text-5xl font-light leading-[1.18] mt-6">{home.manifesto}</p>
        </div>
      </section>

      <section className="bg-cream py-24 px-6 text-center">
        <div className="max-w-2xl mx-auto">
          <p className="text-xs tracking-[0.25em] uppercase text-ink/45 font-semibold">{home.newsletterEyebrow}</p>
          <h2 className="font-marquee text-3xl md:text-5xl font-bold mt-3">{home.newsletterTitle}</h2>
          <p className="mt-4 text-ink/70 text-lg leading-relaxed">{home.newsletterBody}</p>
          <div className="mt-8 max-w-md mx-auto">
            <NewsletterSignup />
          </div>
        </div>
      </section>
    </>
  );
};

export default IntlHome;
